// Database on the shared policy screens (see ../adapterContract.js and the
// reference adapter ./ad.jsx). Who = IAM users only: the backend requires at
// least one permissions.iam_users email and ignores iam_groups, so there is
// no Everyone and no Groups. Where = host, database, type, role, privileges,
// tables and field masking (MySQL only). Advanced has Time and Network but
// not Privilege (not reported for database logins yet).
//
// Differences from the other products: a blank policyFlow means Require MFA
// (the backend fills that in, and new policies default to it), and Require
// MFA also wins over Allow. Create answers 409 for an exact duplicate (the
// shell offers "Edit that policy" and "Create anyway") and a warning for
// other overlaps.
import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Checkbox, Radio, Select } from "antd";
import axios from "axios";

import { AUTH_TOKEN, DOMAIN_ID, ORG_ID, REACT_APP_API } from "../../../constants";
import { approvePolicyAgentless, createADPolicy, listADPolicies, policyAction, updateADPolicy } from "../../../service/adPolicyService";
import { deletePolicyJSON } from "../../../service/appService";
import { getDatabaseHostList, getDatabaseUsersList, getDatabasesList, getTableFields, getTableLists } from "../../../service/databaseService";
import { normalizeDbPolicyFlow } from "../../enduser/db-policies/dbPolicyModel";
import { callService, defineAdapter, listFilters, updatedPolicyId } from "../adapterContract";
import { Field } from "../ui";
import { fieldDomId } from "../editor/draft";

const ids = () => ({ orgId: ORG_ID(), tenantId: DOMAIN_ID() });
const list = (v) => (Array.isArray(v) ? v.filter((x) => x !== null && x !== undefined && String(x).trim() !== "").map(String) : []);

export const DB_TYPES = [
  { value: "mysql", label: "MySQL" },
  { value: "postgres", label: "Postgres" },
];
export const DB_PRIVILEGES = [
  { value: "READ", label: "Read" },
  { value: "WRITE", label: "Write" },
  { value: "EXECUTE", label: "Execute" },
];

/** "PostgreSQL", "postgres", "MySQL", "mariadb" -> "postgres" | "mysql" | "". */
export function dbTypeOf(raw) {
  const v = String(raw || "").trim().toLowerCase();
  if (v.startsWith("postgres") || v === "pg") return "postgres";
  if (v.includes("mysql") || v.includes("maria")) return "mysql";
  return "";
}
const typeLabel = (t) => DB_TYPES.find((x) => x.value === t)?.label || "";

// Only the picked tables, only tables with columns.
function cleanMasking(masking, tables) {
  const keep = new Set(tables);
  const out = {};
  Object.entries(masking || {}).forEach(([table, cols]) => {
    const c = list(cols);
    if (keep.has(table) && c.length) out[table] = c;
  });
  return out;
}

/* ---------------------------------------------------------------- lookups */

const call = (fn, payload) => new Promise((resolve, reject) => fn({ payload, onSuccess: resolve, onFailure: reject }));

// IAM users, as the old form loaded them (walletService/walletUserList),
// fetched once and filtered here as the admin types.
let iamUsers = null;
const loadIamUsers = () => {
  if (!iamUsers) {
    iamUsers = axios
      .post(
        `${REACT_APP_API}/api/v1/walletService/walletUserList`,
        { orgId: ORG_ID(), domainId: DOMAIN_ID(), pageNumber: 1, pageSize: 200, search: "", filter: "" },
        { headers: { withCredentials: true, "X-Authorization": AUTH_TOKEN() } },
      )
      .then((res) => list((res?.data?.users || []).map((u) => u?.emailAddress)))
      .catch(() => {
        iamUsers = null;
        throw new Error("Couldn't load IAM users.");
      });
  }
  return iamUsers;
};
const searchIamUsers = (q) => {
  const needle = String(q || "").trim().toLowerCase();
  return loadIamUsers().then((users) => users.filter((u) => !needle || u.toLowerCase().includes(needle)).map((u) => ({ value: u })));
};

// One lookup keyed by its inputs; null until the answer for `key` is in.
function useLookup(key, load) {
  const [state, setState] = useState({ key: null, value: null });
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    if (key === null) return undefined;
    let live = true;
    Promise.resolve()
      .then(() => loadRef.current())
      .then((value) => live && setState({ key, value }))
      .catch(() => live && setState({ key, value: [] }));
    return () => {
      live = false;
    };
  }, [key]);
  return key !== null && state.key === key ? state.value : null;
}

/* ------------------------------------------------------------------- data */

function useDatabaseData(draft, setProduct) {
  const location = useLocation();
  const p = draft?.product || null;
  const host = p?.host || "";
  const database = p?.database || "";
  const tables = list(p?.tables);
  const mysql = p?.databaseType === "mysql";

  const hosts = useLookup(p ? "hosts" : null, () =>
    call(getDatabaseHostList, { ...ids(), limit: 100, pageId: 1, filters: [] }).then((r) => list(r?.data)),
  );
  const databases = useLookup(p && host ? `db:${host}` : null, () =>
    call(getDatabasesList, { ...ids(), limit: 100, filters: [{ filterParameter: "Host", filterValue: host }] }).then((r) =>
      (Array.isArray(r?.data) ? r.data : []).filter((d) => d?.db_name).map((d) => ({ name: d.db_name, type: dbTypeOf(d.db_type) })),
    ),
  );
  const roles = useLookup(p && database ? `roles:${database}` : null, () =>
    call(getDatabaseUsersList, { ...ids(), limit: 100, requestId: "", filters: [{ filterParameter: "Database", filterValue: database }] }).then((r) =>
      list((Array.isArray(r?.data) ? r.data : []).map((u) => u?.username)),
    ),
  );
  const tableNames = useLookup(p && database ? `tables:${database}` : null, () =>
    call(getTableLists, { ...ids(), DbName: database }).then((r) => list(r?.table_names)),
  );
  const columnsKey = p && database && mysql && tables.length ? `cols:${database}:${tables.join(",")}` : null;
  const columns = useLookup(columnsKey, () =>
    call(getTableFields, { ...ids(), tenantName: database, tableNames: tables }).then((r) => (r?.fields && typeof r.fields === "object" ? r.fields : {})),
  );

  // "Grant access" on the database screens opens a new policy with the
  // database picked: navigate(policiesPath("database", "new"), { state: { databasePrefill } }).
  const prefill = location?.state?.databasePrefill;
  const prefilled = useRef(false);
  useEffect(() => {
    if (!p || prefilled.current || !prefill || p.host || p.database) return;
    prefilled.current = true;
    const t = list(prefill.tables);
    setProduct(
      { host: prefill.host || "", database: prefill.database || "", databaseType: dbTypeOf(prefill.databaseType), tables: t, fillTables: !t.length },
      { silent: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p, prefill]);

  // A stored policy without database_type: read it from the database list.
  useEffect(() => {
    if (!p || p.databaseType || !databases) return;
    const t = databases.find((d) => d.name === database)?.type;
    if (t) setProduct({ databaseType: t }, { silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [databases, database, p?.databaseType]);

  // Picking a database picks every table, as the old form did.
  useEffect(() => {
    if (!p?.fillTables || !tableNames) return;
    setProduct({ tables: tableNames, fillTables: false }, { silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableNames, p?.fillTables]);

  return {
    hosts: hosts || [],
    hostsLoaded: hosts !== null,
    databases: databases || [],
    databasesLoaded: databases !== null,
    roles: roles || [],
    rolesLoaded: roles !== null,
    tables: tableNames || [],
    tablesLoaded: tableNames !== null,
    columns: columns || {},
  };
}

/* -------------------------------------------------------------- sections */

const SEP = "\u0000";
const half = { flex: "1 1 14rem", minWidth: 0 };

function Where({ product, setProduct, errors, touch, data }) {
  const p = product;
  const tables = list(p.tables);
  const mysql = p.databaseType === "mysql";
  const keep = (v, opts) => (v && !opts.includes(v) ? [v, ...opts] : opts);

  const hostOptions = keep(p.host, data?.hosts || []).map((h) => ({ value: h, label: h }));
  const dbOptions = (() => {
    const known = data?.databases || [];
    const out = known.map((d) => ({ value: d.name, label: d.name, dbType: d.type }));
    return p.database && !known.some((d) => d.name === p.database) ? [{ value: p.database, label: p.database }, ...out] : out;
  })();
  const roleOptions = keep(p.role, data?.roles || []).map((r) => ({ value: r, label: r }));
  const tableOptions = [...new Set([...tables, ...(data?.tables || [])])].map((t) => ({ value: t, label: t }));

  const masking = p.fieldMasking || {};
  const maskValue = Object.entries(masking).flatMap(([t, cols]) => list(cols).map((c) => `${t}${SEP}${c}`));
  const maskOptions = tables
    .map((t) => {
      const cols = [...new Set([...list(masking[t]), ...list(data?.columns?.[t])])];
      return cols.length ? { label: t, title: t, options: cols.map((c) => ({ value: `${t}${SEP}${c}`, label: c })) } : null;
    })
    .filter(Boolean);
  const setMasking = (values) => {
    const next = {};
    values.forEach((v) => {
      const [t, c] = String(v).split(SEP);
      if (t && c) next[t] = [...(next[t] || []), c];
    });
    setProduct({ fieldMasking: next });
  };

  return (
    <>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
        <Field label="Host" required error={errors["where.host"]} htmlFor={fieldDomId("where.host")} style={half}>
          <Select
            id={fieldDomId("where.host")}
            aria-label="Host"
            showSearch
            value={p.host || undefined}
            placeholder={data?.hostsLoaded ? "Pick a host" : "Loading hosts…"}
            options={hostOptions}
            notFoundContent={data?.hostsLoaded ? "No database hosts yet" : "Loading…"}
            onChange={(host) => setProduct({ host, database: "", databaseType: "", role: "", tables: [], fieldMasking: {}, fillTables: false })}
            onBlur={() => touch("where.host")}
            status={errors["where.host"] ? "error" : undefined}
            style={{ width: "100%" }}
          />
        </Field>
        <Field label="Database" required error={errors["where.database"]} htmlFor={fieldDomId("where.database")} style={half}>
          <Select
            id={fieldDomId("where.database")}
            aria-label="Database"
            showSearch
            disabled={!p.host && !p.database}
            value={p.database || undefined}
            placeholder={p.host ? "Pick a database" : "Pick a host first"}
            options={dbOptions}
            notFoundContent={data?.databasesLoaded ? "No databases on this host" : "Loading…"}
            onChange={(database, opt) =>
              setProduct({ database, databaseType: opt?.dbType || p.databaseType || "", role: "", tables: [], fieldMasking: {}, fillTables: true })
            }
            onBlur={() => touch("where.database")}
            status={errors["where.database"] ? "error" : undefined}
            style={{ width: "100%" }}
          />
        </Field>
      </div>
      <Field label="Type" required error={errors["where.databaseType"]} help="Filled in from the database you pick.">
        <Radio.Group
          id={fieldDomId("where.databaseType")}
          aria-label="Type"
          value={p.databaseType || undefined}
          onChange={(e) => {
            setProduct({ databaseType: e.target.value });
            touch("where.databaseType");
          }}
          options={DB_TYPES}
        />
      </Field>
      <Field label="Role" required error={errors["where.role"]} help="The database account the session runs as." htmlFor={fieldDomId("where.role")}>
        <Select
          id={fieldDomId("where.role")}
          aria-label="Role"
          showSearch
          disabled={!p.database}
          value={p.role || undefined}
          placeholder={p.database ? "Pick a role" : "Pick a database first"}
          options={roleOptions}
          notFoundContent={data?.rolesLoaded ? "No roles in this database" : "Loading…"}
          onChange={(role) => setProduct({ role })}
          onBlur={() => touch("where.role")}
          status={errors["where.role"] ? "error" : undefined}
          style={{ width: "100%" }}
        />
      </Field>
      <Field label="Privileges" required error={errors["where.privileges"]}>
        <Checkbox.Group
          id={fieldDomId("where.privileges")}
          aria-label="Privileges"
          value={list(p.privileges)}
          onChange={(v) => {
            setProduct({ privileges: DB_PRIVILEGES.map((x) => x.value).filter((x) => v.includes(x)) });
            touch("where.privileges");
          }}
          options={DB_PRIVILEGES}
        />
      </Field>
      <Field label="Tables" help="Every table is picked when you choose the database." htmlFor={fieldDomId("where.tables")}>
        <Select
          id={fieldDomId("where.tables")}
          aria-label="Tables"
          mode="multiple"
          allowClear
          disabled={!p.database}
          value={tables}
          placeholder={p.database ? "Pick tables" : "Pick a database first"}
          options={tableOptions}
          notFoundContent={data?.tablesLoaded ? "No tables in this database" : "Loading…"}
          onChange={(v) => setProduct({ tables: v, fieldMasking: cleanMasking(masking, v), fillTables: false })}
          maxTagCount="responsive"
          style={{ width: "100%" }}
        />
      </Field>
      {mysql && tables.length > 0 && (
        <Field label="Field masking" help="Picked columns come back masked." htmlFor={fieldDomId("where.fieldMasking")}>
          <Select
            id={fieldDomId("where.fieldMasking")}
            aria-label="Field masking"
            mode="multiple"
            allowClear
            value={maskValue}
            placeholder="Pick columns to mask"
            options={maskOptions}
            notFoundContent="No columns found"
            onChange={setMasking}
            optionFilterProp="label"
            style={{ width: "100%" }}
          />
        </Field>
      )}
    </>
  );
}

/* ------------------------------------------------------------ words */

const privilegeText = (privs) => {
  const p = list(privs);
  return p.length ? p.map((v) => DB_PRIVILEGES.find((x) => x.value === v)?.label.toLowerCase() || v).join(", ") : "";
};

function maskingText(masking) {
  const entries = Object.entries(masking || {}).filter(([, c]) => list(c).length);
  return entries.map(([t, c]) => `${t}: ${list(c).join(", ")}`).join("; ");
}

/* --------------------------------------------------------------- adapter */

const databaseAdapter = defineAdapter({
  key: "database",
  policyType: "database",
  modeKey: "mfa_db",
  labels: {
    product: "database",
    eyebrow: "Database · Policies",
    listDescription: "Who connects to which databases, with which privileges, and when MFA is required.",
    emptySentence: "Without a policy, logins are allowed.",
    subject: "login",
  },

  list: () =>
    callService(listADPolicies, { ...ids(), policyType: "database", filters: listFilters("database"), page: 1, limit: 500 }).then((res) =>
      (res?.policies || res?.data || []).filter((p) => String(p?.policyType || p?.policyJson?.policyType || "").toLowerCase() === "database"),
    ),
  create: ({ policyName, policyJson, force }) =>
    callService(createADPolicy, { ...ids(), policyName, policyType: "database", policyJson, generatedBy: "Manual", ...(force ? { force: true } : {}) }).then((res) => ({
      id: typeof res?.data === "string" ? res.data : null,
      warning: res?.warning || "",
    })),
  // Every update makes a new policy (new id).
  update: ({ id, policyName, policyJson, stored }) =>
    callService(updateADPolicy, {
      id,
      ...ids(),
      policyName,
      policyType: "database",
      policyJson,
      status: stored?.status || "Approved",
      version: (Number(stored?.version) || 1) + 1,
      updatedBy: localStorage.getItem("userId") || "",
    }).then((res) => ({ id: updatedPolicyId(res, id), warning: res?.warning || "" })),
  // Only Approved policies are evaluated. Switching on a suspended policy
  // resumes it; one that was never approved goes through the agentless
  // approval, as the old list's Approve did.
  isActive: (row) => String(row?.status || "Approved") === "Approved",
  setActive: (row, active) => {
    const id = row.id || row.policyId;
    if (active && String(row?.status || "") !== "Suspended") return callService(approvePolicyAgentless, { ...ids(), policyId: id });
    return callService(policyAction, { ...ids(), action: { actionType: "status", actionIds: [id], actionValue: active ? "Approved" : "Suspended" } });
  },
  remove: (row) => callService(deletePolicyJSON, { ...ids(), policyId: row.id || row.policyId }),

  // Blank reads as Require MFA (the backend's default); a value the engine
  // doesn't recognise refuses the login, so it reads as Deny.
  actionOf: (policyJson) => normalizeDbPolicyFlow(policyJson?.policyFlow) || "block",

  emptyDraft: () => ({
    action: "mfa_required",
    who: { everyone: false, users: [] },
    product: { host: "", database: "", databaseType: "", role: "", privileges: [], tables: [], fieldMasking: {} },
  }),
  fromPolicy: (row) => {
    const j = row?.policyJson || {};
    const db = j.database || {};
    const tables = list(db.tables);
    return {
      who: { everyone: false, users: list(j.permissions?.iam_users) },
      product: {
        host: db.host || "",
        database: db.database_name || "",
        databaseType: dbTypeOf(db.database_type),
        role: db.user || "",
        privileges: list(db.privilege),
        tables,
        fieldMasking: cleanMasking(db.field_masking, tables),
      },
    };
  },
  toPolicyJson: (draft, stored) => {
    const s = stored || {};
    const p = draft.product;
    const tables = list(p.tables);
    return {
      ...s,
      policyName: draft.policyName,
      policyType: "database",
      database: {
        ...(s.database || {}),
        host: p.host,
        database_name: p.database,
        database_type: p.databaseType,
        user: p.role,
        privilege: list(p.privileges),
        tables,
        // Postgres has no field masking: the agent expects null there.
        field_masking: p.databaseType === "postgres" ? null : cleanMasking(p.fieldMasking, tables),
      },
      permissions: {
        ...(s.permissions || {}),
        allowed: s.permissions?.allowed ?? true,
        iam_users: list(draft.who.users),
        iam_groups: list(s.permissions?.iam_groups),
      },
    };
  },
  validate: (draft) => {
    const e = {};
    const p = draft.product;
    if (!p.host) e["where.host"] = "Pick a host.";
    if (!p.database) e["where.database"] = "Pick a database.";
    if (!p.databaseType) e["where.databaseType"] = "Pick MySQL or Postgres.";
    if (!p.role) e["where.role"] = "Pick a role.";
    if (!list(p.privileges).length) e["where.privileges"] = "Pick at least one privilege.";
    return e;
  },

  useData: useDatabaseData,
  Where,
  who: {
    everyone: false,
    except: true,
    fields: [{ key: "users", kind: "users", label: "Users", placeholder: "Search IAM users", search: searchIamUsers, except: "users" }],
  },

  listWhere: (row) => {
    const db = row?.policyJson?.database || {};
    if (!db.database_name) return "";
    return db.user ? `${db.database_name} as ${db.user}` : db.database_name;
  },
  sentenceWhere: (draft) => {
    const p = draft.product;
    if (!p.database) return "";
    return `on ${p.database}${p.role ? ` as ${p.role}` : ""}`;
  },
  reviewRows: (draft) => {
    const p = draft.product;
    const type = typeLabel(p.databaseType);
    const tables = list(p.tables);
    const rows = [
      {
        key: "database",
        label: "Database",
        value: p.database ? `${p.database}${p.host ? ` on ${p.host}` : ""}${type ? ` (${type})` : ""}` : "None picked",
        section: "where",
      },
      {
        key: "role",
        label: "Role",
        value: p.role ? `${p.role}${privilegeText(p.privileges) ? `, ${privilegeText(p.privileges)}` : ""}` : "None picked",
        section: "where",
      },
      { key: "tables", label: "Tables", value: tables.length ? tables.join(", ") : "None picked", section: "where" },
    ];
    const masked = p.databaseType === "mysql" ? maskingText(cleanMasking(p.fieldMasking, tables)) : "";
    if (masked) rows.push({ key: "masking", label: "Field masking", value: masked, section: "where" });
    return rows;
  },

  features: {
    // A database fallback is never matched: logins no policy covers follow
    // the tenant's noPolicyAction setting instead.
    fallback: false,
    fallbackNote: "Logins no policy covers follow the \"Logins no policy covers\" setting on the Databases page (allow, require MFA or deny).",
    approval: {
      search: (q) => searchIamUsers(q),
      desc: "The approvers get a push at the same time as the person's own MFA push; the login goes through when both approve. Nobody can approve their own login.",
      note: "Name people: approver groups are stored but not resolved yet. The person and the approver each have 60 seconds to answer.",
      validLabel: "Approval valid for",
    },
    // Max length and idle timeout; ProxySQL ends the session at the limit.
    session: { record: false, reauth: false },
    sessionNote: (draft) => (draft.product?.databaseType === "mysql" ? "Session limits are enforced for Postgres only." : ""),
    mfaWinsOverAllow: true,
    actionSentences: { allow: "Allow: the login goes through with no second factor, unless a Require MFA policy also matches." },
  },
});

export default databaseAdapter;
